<template>
  <v-dialog v-model="internalOpen" v-if="order" persistent max-width="800px" @keydown.esc="onClose">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-cart-plus</v-icon>
        Νέα Προπαραγγελία από Παραγγελία Προμηθευτή
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-container fluid>
          <!-- Read-only order/part summary -->
          <v-row dense>
            <v-col cols="12">
              <v-alert type="info" outlined>
                <div class="font-weight-medium mb-1">
                  {{ order.Manufacturer }} — {{ order.FmSku }}
                </div>
                <div class="text--secondary">
                  {{ order.Description }}
                </div>
                <div class="mt-2 d-flex flex-wrap">
                  <span class="mr-4">Κωδ. Κατασκ.: <strong>{{ order.ManufacturerSku }}</strong></span>
                  <span class="mr-4">Κωδ. Προμηθ.: <strong>{{ order.SupplierSku }}</strong></span>
                  <span class="mr-4">Κατηγορία: <strong>{{ categoryText }}</strong></span>
                  <span v-if="order.SuggestedPrice" class="mr-4">Λιανική: <strong>{{ $tocurr(order.SuggestedPrice || 0) }}</strong></span>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Editable preorder fields -->
          <v-form ref="form">
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field label="Κωδ. Κατασκευαστή" v-model="localItem.ManufacturerSku" readonly hide-details="auto" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field label="Κωδ. Προμηθευτή" v-model="localItem.SupplierSku" readonly hide-details="auto" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-combobox label="Κατασκευαστής" :items="manufacturers" v-model="localItem.Manufacturer" hide-details="auto" />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12">
                <!-- Part locked to this order's part -->
                <v-text-field
                  label="Ανταλλακτικό"
                  :value="partSummary"
                  readonly
                  prepend-inner-icon="mdi-cog"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="12">
                <v-text-field label="Σχόλια" v-model="localItem.Remarks" hide-details="auto" />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="localItem.RepairId"
                  :items="repairsForVehicle"
                  :loading="loadingRepairs"
                  hide-details
                  clearable
                  item-text="label"
                  item-value="Id"
                  label="Επισκευή"
                  prepend-icon="mdi-car-wrench"
                  :disabled="!!localItem.VehicleId"
                  hint="Επιλέξτε Επισκευή ή Όχημα"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="localItem.VehicleId"
                  :items="vehicles"
                  :loading="loadingVehicles"
                  hide-details
                  clearable
                  :item-text="vehicleItemText"
                  item-value="Id"
                  label="Όχημα πελάτη"
                  prepend-icon="mdi-truck"
                  :disabled="!!localItem.RepairId"
                />
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="supplier.Name"
                  hide-details
                  readonly
                  label="Προμηθευτής"
                  prepend-icon="mdi-truck"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="3">
                <v-text-field label="Ποσότητα" v-model.number="localItem.Qty" type="number" min="1" hide-details="auto" />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select label="Μονάδα Μέτρησης" v-model="localItem.Unit" :items="['ΤΕΜ', 'ΣΕΤ', 'ΚΙΛΑ', 'ΛΙΤΡΑ', 'ΜΕΤΡΟ', 'ΩΡΑ','ΚΥΒΙΚΑ', '']" hide-details />
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field label="Τιμή" v-model.number="localItem.Price" type="number" hide-details="auto" />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select label="Κατάσταση"  v-model="localItem.Status" :items="statuses" item-text="Description" item-value="Id" hide-details />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select label="Τεχνικός" v-model="localItem.TechId" :items="technicians" item-text="Name" item-value="Id" hide-details />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onClose">Κλείσιμο</v-btn>
        <v-btn color="primary" text @click="onSave">Αποθήκευση</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'DistributorOrderPreorderDialog',
  props: {
    value: { type: Boolean, default: false }, // dialog visibility
    order: { type: Object, default: null }, // current row item (order)
    // data sources for selectors
    vehicles: { type: Array, default: () => [] },
    supplier: { type: Object, default: null },
    technicians: { type: Array, default: () => [] },
    partCategories: { type: Array, default: () => [] },
    statuses: { type: Array, default: () => [] },
    repairsForVehicle: { type: Array, default: () => [] },
    // loading flags
    loadingVehicles: { type: Boolean, default: false },
    loadingRepairs: { type: Boolean, default: false },
  },
  data() {
    return {
      internalOpen: this.value,
      localItem: this.initFromOrder(this.order),
      selectedVehicleLocal: {Id: null},
      selectedSupplierLocal: null,
      rules: {
        required: v => (!!v || v === 0) || 'Required.',
      },
    };
  },
  computed: {
    manufacturers() {
      return this.$manufacturers.map(e=>e.title);
    },
    categoryText() {
      const c = this.partCategories.find(i => i.Id = this.order.CategoryId).Description || this.order.CategoryId;
      if (typeof c === 'string') return c;
      return c || '';
    },
    partSummary() {
      if (!this.order) return '';
      return `${this.order.PartId}: ${this.order.FmSku} - ${this.order.Description}`;
    },
  },
  watch: {
    value(v) { this.internalOpen = v; if (v) this.hydrateSelections(); },
    internalOpen(v) { this.$emit('input', v); if (!v) this.resetState(); },
    order: {
      deep: true,
      handler(v) { this.localItem = this.initFromOrder(v); this.$nextTick(this.hydrateSelections); }
    },
    selectedVehicleLocal(v) {
      const vid = v && (v.Id || v);
      this.localItem.VehicleId = vid || null;
      if (vid) {
        // if vehicle is chosen, clear RepairId to enforce either-or
        this.localItem.RepairId = null;
        this.$emit('fetch-repairs-for-vehicle', vid);
      }
    },
  },
  methods: {
    initFromOrder(order) {
      const basePrice = order.SuggestedPrice;
      return {
        Id: 0,
        ManufacturerSku: order.ManufacturerSku || null,
        Manufacturer: order.Manufacturer || null,
        SupplierSku: order.SupplierSku || null,
        SupplierId: this.supplier.Id || null,
        CustomerId: null,
        VehicleId: null,
        RepairId: null,
        Remarks: '',
        Qty: 1,
        Unit: 'ΤΕΜ',
        Price: basePrice,
        Status: -2, // default like Preorders.vue
        TechId: 1,
        PartId: order.PartId || null,
      };
    },
    resetState() {
      this.$nextTick(() => {
        this.localItem = this.initFromOrder(this.order);
        // this.selectedVehicleLocal = null;
        this.selectedSupplierLocal = this.supplier.Name;
      });
    },
    hydrateSelections() {
      if (!this.order) return;
      // preselect supplier from order invoice if available
      this.selectedSupplierLocal = this.supplier.Name;
      console.log(this.statuses)
    },
    vehicleItemText(item) {
      if (!item) return '';
      const cust = item.Owner && item.Owner.Name ? item.Owner.Name : (item.Customer && item.Customer.Name) || '';
      const plate = item.PlateNumber || '';
      return `${cust} — ${plate}`;
    },
    onVehicleChange() {
      if (this.selectedVehicleLocal) {
        console.log('Vehicle selected:', this.selectedVehicleLocal);
      }
      // const vid = this.selectedVehicleLocal && (this.selectedVehicleLocal.Id);
      // if (vid) this.$emit('fetch-repairs-for-vehicle', vid);
    },
    onClose() {
      this.internalOpen = false;
      this.$emit('input', false);
      this.$emit('close');
    },
    onSave() {
      const form = this.$refs.form;
      if (form && form.validate && !form.validate()) return;

      // simple either-or validation
      if (!this.localItem.VehicleId && !this.localItem.RepairId) {
        this.$emit('validation-error', 'Επιλέξτε είτε Όχημα είτε Επισκευή');
        return;
      }
      this.$emit('save', { ...this.localItem });
    },
  },
};
</script>